/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRemoteMcpServersRequest,
  ListRemoteMcpServersSecurity,
} from "../models/operations/listremotemcpservers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRemoteMcpServersQuery,
  prefetchRemoteMcpServers,
  queryKeyRemoteMcpServers,
  RemoteMcpServersQueryData,
} from "./remoteMcpServers.core.js";
export {
  buildRemoteMcpServersQuery,
  prefetchRemoteMcpServers,
  queryKeyRemoteMcpServers,
  type RemoteMcpServersQueryData,
};

export type RemoteMcpServersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listServers remoteMcp
 *
 * @remarks
 * List all remote MCP servers for a project
 */
export function useRemoteMcpServers(
  request?: ListRemoteMcpServersRequest | undefined,
  security?: ListRemoteMcpServersSecurity | undefined,
  options?: QueryHookOptions<
    RemoteMcpServersQueryData,
    RemoteMcpServersQueryError
  >,
): UseQueryResult<RemoteMcpServersQueryData, RemoteMcpServersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRemoteMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listServers remoteMcp
 *
 * @remarks
 * List all remote MCP servers for a project
 */
export function useRemoteMcpServersSuspense(
  request?: ListRemoteMcpServersRequest | undefined,
  security?: ListRemoteMcpServersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RemoteMcpServersQueryData,
    RemoteMcpServersQueryError
  >,
): UseSuspenseQueryResult<
  RemoteMcpServersQueryData,
  RemoteMcpServersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRemoteMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRemoteMcpServersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RemoteMcpServersQueryData,
): RemoteMcpServersQueryData | undefined {
  const key = queryKeyRemoteMcpServers(...queryKeyBase);

  return client.setQueryData<RemoteMcpServersQueryData>(key, data);
}

export function invalidateRemoteMcpServers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteMcp", "listServers", ...queryKeyBase],
  });
}

export function invalidateAllRemoteMcpServers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteMcp", "listServers"],
  });
}
